import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import api from '../utils/apiConfig';

const AuthContext = createContext();

// Helper function to get stored user data
const getStoredUser = () => {
  try {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
};

// Helper function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const storedToken = localStorage.getItem('token');
const storedUser = getStoredUser();
const isTokenValid = storedToken && !isTokenExpired(storedToken);

const initialState = {
  user: isTokenValid ? storedUser : null,
  token: isTokenValid ? storedToken : null,
  isAuthenticated: isTokenValid && !!storedUser,
  loading: isTokenValid && !storedUser, // Only loading if we have token but no user data
  error: null,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    case 'LOAD_USER_SUCCESS':
      localStorage.setItem('user', JSON.stringify(action.payload));
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOAD_USER_FAILURE':
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set default axios header
  useEffect(() => {
    // Removed manual header setting as it's handled by api interceptor
    return;
  }, [state.token]);

  const loadUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      dispatch({
        type: 'LOAD_USER_SUCCESS',
        payload: response.data.data.user,
      });
    } catch (error) {
      dispatch({ type: 'LOAD_USER_FAILURE' });
    }
  }, []);

  const login = useCallback(async (email, password) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.data.data.user,
          token: response.data.data.token,
        },
      });
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  }, []);

  const register = useCallback(async ({ firstName, lastName, email, password, phone, locationName }) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await api.post('/auth/register', {
        firstName,
        lastName,
        email,
        password,
        phone,
        locationName,
      });

      // Reuse LOGIN_SUCCESS to store token and user
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.data.data.user,
          token: response.data.data.token,
        },
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(() => {
    dispatch({ type: 'LOGOUT' });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Only load user from server if we have token but no user data
  useEffect(() => {
    if (state.token && !state.user && state.loading) {
      loadUser();
    } else if (!state.token) {
      dispatch({ type: 'LOAD_USER_FAILURE' });
    }
  }, [state.token, state.user, state.loading, loadUser]);

  const value = {
    ...state,
    login,
    register,
    logout,
    clearError,
    loadUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
